import Calendar from 'react-calendar';
import styled from 'styled-components';

import TextInput, { LinkableTextInput } from 'components/ui/TextInput';
import Select from 'components/ui/Select';
import { Row } from 'components/shared/Layout';
import Button from 'components/ui/Button';
import { colours, common } from 'themes/default';

export const StyledDescription = styled.div`
  font-size: 16px;
  background: ${colours.Greyscale.MistGrey};
  color: ${colours.Shades.BlueShade40};
  padding: 0.6em 1.4em 0.5em 0.8em;
`;

export const CtaButton = styled(Button)`
  width: 100%;
  margin: 4px 0;
  background: ${colours.Core.SecondaryPlum};
  border: none;
  border-radius: 2px;
  padding: 0px;

  > div {
    color: white;
  }

  &:hover {
    background: ${colours.Core.SecondaryPlum};
    opacity: 0.5;
    > div {
      color: white;
    }
  }

  &:disabled,
  &[disabled] {
    background: ${colours.Greyscale.SilverGrey};
    > div {
      color: ${colours.Greyscale.CharcoalGrey};
    }
  }
`;

export const FormContainer = styled.div`
  background: ${colours.Greyscale.MistGrey};
  border: 1px solid ${colours.Greyscale.SilverGrey};
  margin-bottom: 20px;
  padding: 20px;
`;

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    list-style: none;
    margin: 0 0 20px 0;
    padding-left: 0;
  }

  .page-item {
    height: 3rem;
    width: 3rem;

    &:first-child .page-link {
      margin-left: 0;
    }
  }

  .page-link {
    align-items: center;
    background-color: ${common.pagination.normal.backgroundColour};
    border: 0.125rem solid ${common.pagination.normal.borderColour};
    border-radius: 0.25rem;
    color: ${common.pagination.normal.fontColour};
    cursor: pointer;
    display: flex;
    font-weight: normal;
    height: 100%;
    justify-content: center;
    outline: none;
    position: relative;
    width: 100%;
    text-decoration: none;

    &:hover {
      background-color: ${common.pagination.hover.backgroundColour};
      border-color: ${common.pagination.hover.borderColour};
      color: ${common.pagination.hover.fontColour};
      text-decoration: underline;
      z-index: 2;
    }

    &:active,
    &:focus-visible {
      background-color: ${common.pagination.focus.backgroundColour};
      border-color: ${common.pagination.focus.borderColour};
      color: ${common.pagination.focus.fontColour};
      z-index: 3;
    }

    /* Active */
    &[aria-current='page'] {
      color: ${common.pagination.active.fontColour};
      background-color: ${common.pagination.active.backgroundColour};
      border-color: ${common.pagination.active.borderColour};
      font-weight: bold;
      z-index: 3;

      &:active,
      &:focus-visible {
        border-color: ${common.pagination.activeFocus.borderColour};
        outline: 0.125rem solid ${common.pagination.activeFocus.outlineColour};
      }
    }

    /* Disabled */
    &[aria-disabled='true'] {
      pointer-events: none;
      color: ${common.pagination.disabled.fontColour};
      background-color: ${common.pagination.disabled.backgroundColour};
      border-color: ${common.buttons.disabled.borderColour};
      cursor: not-allowed;
    }
  }
`;

export const StyledLabel = styled.label`
  display: block;
  padding-bottom: 8px;
`;

export const StyledTextInput = styled(TextInput)`
  width: 90%;
  box-sizing: border-box;
  &:read-only {
    background: ${colours.Greyscale.MistGrey};
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  box-sizing: border-box;
  border-radius: 5px;
`;

export const StyledLinkableTextInput = styled(LinkableTextInput)`
  width: 90%;
  box-sizing: border-box;
  &:read-only {
    background: ${colours.Greyscale.MistGrey};
  }
`;

export const StyledDateInput = styled(StyledTextInput)`
  width: 100%;
  ${({ hide }) => hide && 'display: none;'}
`;

export const DatesWrapper = styled.div`
  display: flex;
  width: 90%;
`;

export const DateItem = styled.div`
  flex: 1;
`;

export const DateSeparator = styled.div`
  flex: 0 0 20px;
  align-self: flex-end;
  text-align: center;
  padding: 0.6em 0 0.5em 0;
`;

export const StyledRow = styled(Row)`
  margin-top: 25px;
`;

export const StyledDatesRow = styled(StyledRow)`
  padding-bottom: 34px;
  border-bottom: 1px solid ${colours.Greyscale.SilverGrey};
`;

export const StyledCreatedInfoRow = styled(Row)`
  padding-top: 14px;
`;

export const StyledH2 = styled.h2`
  font-weight: normal;
  font-size: 20px;
  padding-top: ${(props) => props.paddingTop || 0};
`;

export const ErrorMessage = styled.span`
  font-size: 14px;
  color: red;
  display: block;
`;

export const StyledVoucherNotesContainer = styled.div`
  margin: 10px 0 0 0;
  width: inherit;
  background-color: white;
  box-shadow: 0px 0px 30px ${colours.Greyscale.MistGrey};
`;

export const CancellationReasonSelect = styled.div`
  padding: 10px 10px 10px 0;
`;

export const FlexWrapper = styled.div`
  display: flex;

  & select {
    border-radius: 3px;
  }

  & button {
    margin-top: 10px;
    height: 38px;
  }
`;

export const StyledCoolingOffPeriod = styled.div`
  margin: 15px;
`;

export const StyledMessageCoolingOff = styled.div`
  margin: 10px 0;
`;

export const StyledNoRevert = styled.div`
  color: red;
`;

export const StyledDropdown = styled(StyledSelect)`
  display: block;
  height: 32px;
  margin: 10px 0;
`;

export const StyledCancelBeforePaymentButtons = styled.div`
  display: flex;

  & button:nth-child(2) {
    margin-left: 20px;
  }
`;

export const FormDate = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledForm = styled.form`
  display: flex;
  width: auto;
  flex-direction: column;
  margin-top: 20px;
`;

export const StyledConfirm = styled.div`
  width: 40%;
`;

export const StyledDateField = styled.div`
  margin-right: 20px;
  position: relative;
  width: 45%;

  &:hover {
    cursor: pointer;
  }

  & input:hover {
    cursor: pointer;
  }
`;

export const StyledCalendar = styled(Calendar)`
  &.react-calendar {
    width: 45%;
  }

  .react-calendar__tile-away-dates {
    background-color: rgba(0, 110, 220, 0.5);
  }
`;
